
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { RemixIcon } from "@/components/shared/RemixIcon";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface NavItem {
  name: string;
  path: string;
  icon: string;
}

export function Sidebar({
  open,
  setOpen
}: SidebarProps) {
  const location = useLocation();
  const { signOut } = useAuth();
  
  const navItems: NavItem[] = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: "dashboard-line"
    }, 
    {
      name: "Orders",
      path: "/orders",
      icon: "shopping-bag-line"
    }, 
    {
      name: "Factories",
      path: "/factories",
      icon: "building-2-line"
    }, 
    {
      name: "Products",
      path: "/categories",
      icon: "book-open-line"
    }, 
    {
      name: "Variants",
      path: "/variants",
      icon: "stack-line"
    }, 
    {
      name: "Variant Options",
      path: "/variant-options",
      icon: "list-check-2"
    }, 
    {
      name: "Measurements",
      path: "/variant-measurements",
      icon: "ruler-line"
    }, 
    {
      name: "Users",
      path: "/customers",
      icon: "user-line"
    }, 
    {
      name: "Billing",
      path: "/billing",
      icon: "bank-card-line"
    }, 
    {
      name: "Reports",
      path: "/reports",
      icon: "bar-chart-box-line"
    }, 
    {
      name: "Settings",
      path: "/settings",
      icon: "settings-line"
    }
  ];
  
  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Failed to sign out");
    }
  };
  
  return (
    <aside className={`${open ? "w-64" : "w-20"} bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-300 ease-in-out flex flex-col`}>
      <div className="flex items-center justify-between h-16 px-4 border-b border-sidebar-border">
        <div className={`${!open && "hidden"} flex items-center space-x-2`}>
          <img alt="TailorOS Logo" src="/lovable-uploads/c055118f-5352-4b10-802d-6428aaf76c8b.png" className="h-12 w-auto" />
        </div>
        <button onClick={() => setOpen(!open)} className="p-1.5 rounded-md hover:bg-sidebar-accent text-sidebar-foreground focus:outline-none">
          {open ? 
            <RemixIcon name="arrow-left-s-line" size="lg" /> : 
            <RemixIcon name="arrow-right-s-line" size="lg" />
          }
        </button>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="px-2 space-y-1">
          {navItems.map(item => {
            const isActive = location.pathname === item.path || 
                            (item.path === "/dashboard" && location.pathname === "/");
            return (
              <li key={item.name}>
                <Link 
                  to={item.path} 
                  className={`flex items-center ${open ? "px-4" : "justify-center px-2"} py-3 rounded-md transition-colors duration-200 ${
                    isActive 
                      ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                  }`}
                >
                  <RemixIcon name={item.icon} size="lg" className={`${open && "mr-3"}`} />
                  {open && <span>{item.name}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="p-4 border-t border-sidebar-border">
        <div className={`flex items-center ${!open && "justify-center"}`}>
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
            A
          </div>
          {open && (
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-foreground truncate">Admin User</p>
              <p className="text-xs text-sidebar-foreground/70 truncate">admin@tailoros.com</p>
            </div>
          )}
          
          {open && (
            <button 
              onClick={handleSignOut}
              className="p-1.5 rounded-md hover:bg-sidebar-accent text-sidebar-foreground focus:outline-none"
              title="Sign out"
            >
              <RemixIcon name="logout-box-r-line" size="lg" />
            </button>
          )}
        </div>
        
        {!open && (
          <button 
            onClick={handleSignOut}
            className="mt-4 w-full flex justify-center p-2 rounded-md hover:bg-sidebar-accent text-sidebar-foreground focus:outline-none"
            title="Sign out"
          >
            <RemixIcon name="logout-box-r-line" size="lg" />
          </button>
        )}
      </div>
    </aside>
  );
}
